import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import {
  createExport,
  listExports,
  type ConversationExport,
  type ExportFormat,
} from '../api/exports';
import { getApiClient } from '../api/factory';
import { useAuthStore } from '../store/auth';
import { ApiError } from '../api/client';
import { useT, useLocale, type TFn, type DictKey, type Locale } from '../i18n';
import { PageContainer, PageHeader } from '../components/Page';
import { EmptyState } from '../components/EmptyState';
import { SkeletonList } from '../components/Skeleton';
import { Dialog } from '../components/Dialog';

const QK = ['exports'] as const;

function formatDate(s: string | null, locale: Locale): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString(locale === 'en' ? 'en-US' : 'ru-RU');
  } catch {
    return s;
  }
}

function formatBytes(n: number | null): string {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function statusLabel(s: string, t: TFn): { text: string; color: string } {
  const KEYS: Record<string, { key: DictKey; color: string }> = {
    pending: { key: 'exports.status.queued', color: 'text-slate-500' },
    processing: { key: 'exports.status.processing', color: 'text-accent-600' },
    ready: { key: 'exports.status.ready', color: 'text-emerald-700' },
    failed: { key: 'exports.status.failed', color: 'text-red-600' },
    expired: { key: 'exports.status.expired', color: 'text-slate-400' },
  };
  const e = KEYS[s];
  if (!e) return { text: s, color: 'text-slate-500' };
  return { text: t(e.key), color: e.color };
}

export function ExportsPage() {
  const queryClient = useQueryClient();
  const session = useAuthStore((s) => s.session);
  const isAdmin = session?.operator.role === 'admin';
  const [creating, setCreating] = useState(false);
  const t = useT();
  const [locale] = useLocale();

  const { data: list, isLoading, error } = useQuery<ConversationExport[], Error>({
    queryKey: QK,
    queryFn: () => listExports(getApiClient()),
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      const items = q.state.data;
      const hasActive = items?.some(
        (e) => e.status === 'pending' || e.status === 'processing',
      );
      return hasActive ? 3000 : false;
    },
  });

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t('exports.title')}
        description={t('exports.intro')}
        action={
          isAdmin && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="rounded-md bg-accent-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-accent-700"
            >
              {t('exports.create')}
            </button>
          )
        }
      />

      {isLoading && <SkeletonList ariaLabel={t('common.loading')} />}
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.errorPrefix')}: {error.message}
        </p>
      )}

      {list && list.length === 0 && (
        <EmptyState
          icon={Download}
          title={t('exports.empty')}
          hint={isAdmin ? t('exports.emptyHintAdmin') : t('exports.emptyHintOperator')}
          {...(isAdmin
            ? {
                actionLabel: t('exports.create'),
                onAction: () => setCreating(true),
              }
            : {})}
        />
      )}

      {list && list.length > 0 && (
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-card">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">{t('exports.column.created')}</th>
                <th className="px-4 py-2.5 text-left font-medium">{t('exports.column.format')}</th>
                <th className="px-4 py-2.5 text-left font-medium">{t('exports.column.status')}</th>
                <th className="px-4 py-2.5 text-left font-medium">{t('exports.column.rows')}</th>
                <th className="px-4 py-2.5 text-left font-medium">{t('exports.column.size')}</th>
                <th className="px-4 py-2.5 text-right font-medium" />
              </tr>
            </thead>
            <tbody>
              {list.map((e) => {
                const st = statusLabel(e.status, t);
                return (
                  <tr key={e.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-slate-700">{formatDate(e.createdAt, locale)}</td>
                    <td className="px-4 py-3 font-mono text-xs uppercase text-slate-600">
                      {e.format}
                    </td>
                    <td className={`px-4 py-3 ${st.color}`}>{st.text}</td>
                    <td className="px-4 py-3 text-slate-700">{e.rowsCount ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{formatBytes(e.fileSize)}</td>
                    <td className="px-4 py-3 text-right">
                      {e.status === 'ready' && e.downloadUrl ? (
                        <a
                          href={e.downloadUrl}
                          download
                          className="text-accent-600 hover:text-accent-700"
                        >
                          {t('exports.download')}
                        </a>
                      ) : e.status === 'failed' && e.errorMessage ? (
                        <span className="text-xs text-red-600">{e.errorMessage}</span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateExportModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            queryClient.invalidateQueries({ queryKey: QK });
          }}
        />
      )}
    </PageContainer>
  );
}

function CreateExportModal(props: { onClose: () => void; onCreated: () => void }) {
  const [format, setFormat] = useState<ExportFormat>('csv');
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 3600 * 1000);
  const [from, setFrom] = useState(monthAgo.toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  const mut = useMutation({
    mutationFn: () => createExport(getApiClient(), { format, from, to }),
    onSuccess: () => props.onCreated(),
    onError: (e: unknown) => {
      if (e instanceof ApiError) {
        setError(e.message || e.code);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError(t('common.unknownError'));
      }
    },
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    mut.mutate();
  }

  return (
    <Dialog open onClose={props.onClose} title={t('exports.newTitle')} size="sm">
      <form onSubmit={handleSubmit} className="space-y-3">
        <label className="block text-sm">
          <span className="text-xs text-slate-500">{t('exports.format')}</span>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as ExportFormat)}
            className="mt-1 block w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
          >
            <option value="csv">{t('exports.format.csv')}</option>
            <option value="json">{t('exports.format.json')}</option>
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm">
            <span className="text-xs text-slate-500">{t('exports.range.from')}</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs text-slate-500">{t('exports.range.to')}</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
            />
          </label>
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={mut.isPending}
            className="rounded-md bg-accent-600 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {mut.isPending ? t('exports.creating') : t('exports.submitCreate')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
